/**
 * Native Shopify pack-variant selector.
 * Updates the compact trigger and opens/closes the selection dialog.
 */
class VariantPackSelector extends HTMLElement {
  #userSelected = false;

  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.dialog = this.querySelector('[data-pack-dialog]');
    this.trigger = this.querySelector('[data-pack-trigger]');
    this.closeBtn = this.querySelector('[data-pack-close]');

    if (this.trigger && this.dialog) {
      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openDialog();
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeDialog();
        });
      }

      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) {
          this.closeDialog();
        }
      });

      this.dialog.addEventListener('cancel', () => {
        this.trigger.setAttribute('aria-expanded', 'false');
      });
    }

    this.addEventListener('click', (event) => {
      const option = event.target instanceof Element ? event.target.closest('[data-pack-variant]') : null;
      if (!(option instanceof HTMLButtonElement) || !this.contains(option)) return;
      event.preventDefault();
      this.select(option);
    });

    this.#applyPreselectedVariant();

    // The theme can still move or hydrate the markup around the selector, so run again once the
    // DOM has settled and only then bind to the surrounding card.
    this.#whenReady(() => {
      const scope = this.#getScope();
      if (scope !== document) {
        scope.addEventListener('click', this.#syncBeforeAddToCart, { capture: true });
      }

      this.#applyPreselectedVariant();
      this.#forceQuantityOne();
    });
  }

  openDialog() {
    if (!this.dialog) return;
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    this.trigger?.setAttribute('aria-expanded', 'true');
  }

  closeDialog() {
    if (!this.dialog) return;
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
    this.trigger?.setAttribute('aria-expanded', 'false');
  }

  select(option) {
    this.#userSelected = true;

    this.querySelectorAll('[data-pack-variant]').forEach((item) => {
      const selected = item === option;
      item.classList.toggle('is-selected', selected);
      item.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    // Update compact trigger UI in card
    if (this.trigger) {
      const titleEl = this.querySelector('[data-pack-trigger-title]');
      const unitEl = this.querySelector('[data-pack-trigger-unit]');
      const priceEl = this.querySelector('[data-pack-trigger-price]');
      const compareEl = this.querySelector('[data-pack-trigger-compare]');

      if (titleEl && option.dataset.title) titleEl.textContent = option.dataset.title;
      if (unitEl && option.dataset.unitFormatted) unitEl.textContent = option.dataset.unitFormatted;
      if (priceEl && option.dataset.priceFormatted) priceEl.textContent = option.dataset.priceFormatted;
      if (compareEl) {
        if (option.dataset.hasCompare === 'true' && option.dataset.comparePriceFormatted) {
          compareEl.textContent = option.dataset.comparePriceFormatted;
          compareEl.classList.remove('is-hidden');
        } else {
          compareEl.textContent = '';
          compareEl.classList.add('is-hidden');
        }
      }

      this.closeDialog();
    }

    this.#apply(option);

    this.dispatchEvent(
      new CustomEvent('variant-pack-selector:change', {
        bubbles: true,
        detail: { variantId: option.dataset.variantId, price: Number(option.dataset.price || 0) },
      })
    );
  }

  /**
   * Shopify renders the add-to-cart form and the price with the first available variant, so the
   * pack preselected by the server (10 Pack) has to be pushed into them before the first submit.
   */
  #applyPreselectedVariant() {
    if (this.dataset.defaultApplied !== 'true' || this.#userSelected) return;

    const option = this.#getSelectedOption();
    if (option) this.#apply(option);
  }

  #apply(option) {
    const variantId = option.dataset.variantId;
    if (!variantId) return;

    this.#syncVariant(variantId);
    this.#updatePrices(option);
  }

  #getSelectedOption() {
    return this.querySelector('[data-pack-variant].is-selected');
  }

  #whenReady(callback) {
    const run = () => requestAnimationFrame(callback);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  }

  /** Card on listings, the whole product section on the product page. */
  #getScope() {
    return (
      this.closest(
        'product-card, .custom-product-card, .quick-add-modal, dialog, product-component, .product-information, .product-details, [id^="ProductInformation-"]'
      ) || document
    );
  }

  /** On the product page the price and the sticky bar sit outside the details container. */
  #getPriceScope() {
    return (
      this.closest('product-card, .custom-product-card, .quick-add-modal, dialog') ||
      this.closest('.shopify-section') ||
      document
    );
  }

  /** Re-applies the selection in case the surrounding markup was re-rendered after the last sync. */
  #syncBeforeAddToCart = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const hit = target?.closest(
      'button[name="add"], .custom-product-card__add, shopify-accelerated-checkout, shopify-buy-it-now-button, .shopify-payment-button'
    );
    const inPath = path.some(
      (node) =>
        node instanceof Element &&
        (node.matches('button[name="add"], .custom-product-card__add') ||
          node.tagName === 'SHOPIFY-ACCELERATED-CHECKOUT' ||
          node.tagName === 'SHOPIFY-BUY-IT-NOW-BUTTON' ||
          node.classList.contains('shopify-payment-button'))
    );
    if (!hit && !inPath) return;

    const variantId = this.dataset.selectedVariantId;
    if (variantId) this.#syncVariant(variantId);
    this.#forceQuantityOne();
  };

  /**
   * Pack buttons choose a Shopify variant. Quantity stays 1 so a 10 pack is not added as 10 units.
   */
  #forceQuantityOne() {
    const root = this.#getScope();
    root.querySelectorAll('input[name="quantity"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      input.value = '1';
      input.min = '1';
      input.step = '1';
    });

    root.querySelectorAll('quantity-selector-component').forEach((selector) => {
      if (typeof selector.setValue === 'function') selector.setValue('1');
      if (typeof selector.updateConstraints === 'function') selector.updateConstraints('1', null, '1');
    });

    root.querySelectorAll('product-form-component').forEach((form) => {
      if (form instanceof HTMLElement) form.dataset.quantityDefault = '1';
    });
  }

  #syncVariant(variantId) {
    this.dataset.selectedVariantId = variantId;

    const scope = this.#getScope();
    const productId = this.dataset.productId;
    const formSelector = `product-form-component[data-product-id="${productId}"]`;

    const inputs = new Set(
      scope.querySelectorAll(`${formSelector} input[name="id"], ${formSelector} select[name="id"]`)
    );

    if (scope !== document) {
      scope.querySelectorAll('input[name="id"], select[name="id"]').forEach((input) => inputs.add(input));
    }

    inputs.forEach((input) => {
      input.value = variantId;
    });

    scope.querySelectorAll('.custom-product-card__add[data-variant-id]').forEach((button) => {
      if (button instanceof HTMLElement) button.dataset.variantId = variantId;
    });

    const stickyBar = document.querySelector(`sticky-add-to-cart[data-product-id="${productId}"]`);
    if (stickyBar instanceof HTMLElement) stickyBar.dataset.currentVariantId = variantId;

    this.#forceQuantityOne();
  }

  /**
   * The theme only refreshes prices from a variant-picker section render, which this selector does
   * not trigger, so the rendered prices are rewritten from the selected pack.
   */
  #updatePrices(option) {
    const total = option.dataset.priceFormatted;
    if (!total) return;

    const compare = option.dataset.hasCompare === 'true' ? option.dataset.comparePriceFormatted || '' : '';
    const scope = this.#getPriceScope();

    scope
      .querySelectorAll('[data-card-total-price], product-price .price, .sticky-add-to-cart__price .price')
      .forEach((element) => {
        if (element.classList.contains('compare-at-price')) return;
        this.#setPriceText(element, total);
      });

    scope
      .querySelectorAll('product-price .compare-at-price, .sticky-add-to-cart__price .compare-at-price')
      .forEach((element) => {
        if (!(element instanceof HTMLElement)) return;
        this.#setPriceText(element, compare);
        element.style.display = compare ? '' : 'none';
      });

    const unit = option.dataset.unitFormatted;
    if (unit) {
      scope.querySelectorAll('[data-card-unit-price]').forEach((element) => this.#setPriceText(element, unit));
    }

    const title = option.dataset.title;
    if (title) {
      scope.querySelectorAll('.sticky-add-to-cart__variant').forEach((element) => {
        element.textContent = title;
      });
    }
  }

  /** Keeps the accessibility label the theme renders next to the amount. */
  #setPriceText(element, value) {
    const label = element.querySelector(':scope > .visually-hidden');
    if (label) {
      element.replaceChildren(label, document.createTextNode(` ${value}`));
      return;
    }
    element.textContent = value;
  }
}

if (!customElements.get('variant-pack-selector')) {
  customElements.define('variant-pack-selector', VariantPackSelector);
}
